/**
 * useEnglishLocale — resolves whether the current visitor should see
 * American ("us") or British ("uk") spellings.
 *
 * Priority chain:
 *   1. User's explicitly selected locality (LocalityContext).country
 *   2. IP/GPS auto-detected geo (useGeo)
 *   3. Browser `navigator.language`
 *   4. Fallback: "us"
 *
 * The country → locale mapping is coarse on purpose — Canada uses a
 * British-leaning mix in practice, so it maps to "uk". Adjust here if
 * we ever need Canadian- or Australian-specific spellings.
 */
import { useContext, useMemo } from "react";
import { LocalityContext } from "@/contexts/LocalityContext";
import { useGeo } from "@/lib/useGeo";
import { localiseSpelling } from "@/lib/spelling";

// ISO-3166 alpha-2 codes and full country names → dictionary variant.
const US_COUNTRIES = new Set([
  "us", "united states", "united states of america", "usa",
  "ph", "philippines",   // predominantly American spelling
  "lr", "liberia",
]);

// Everything below leans towards British spelling.
const UK_COUNTRIES = new Set([
  "gb", "uk", "united kingdom", "great britain", "england", "scotland", "wales", "northern ireland",
  "ie", "ireland",
  "au", "australia",
  "nz", "new zealand",
  "za", "south africa",
  "in", "india",
  "pk", "pakistan",
  "lk", "sri lanka",
  "bd", "bangladesh",
  "ng", "nigeria",
  "ke", "kenya",
  "gh", "ghana",
  "sg", "singapore",
  "my", "malaysia",
  "hk", "hong kong",
  "ca", "canada",   // Canadian English leans British for -our, -re, -ise
]);

function classifyCountry(raw) {
  if (!raw) return null;
  const key = String(raw).trim().toLowerCase();
  if (US_COUNTRIES.has(key)) return "us";
  if (UK_COUNTRIES.has(key)) return "uk";
  return null;
}

export function useEnglishLocale() {
  // useContext returns undefined if the provider isn't mounted, without
  // throwing — safer than useLocality() here since the hook may be used
  // in preview/error pages that render outside the LocalityProvider.
  const localityCtx = useContext(LocalityContext);
  const manualCountry = localityCtx?.locality?.country || null;
  const { geo } = useGeo();
  return useMemo(() => {
    const manual = classifyCountry(manualCountry);
    if (manual) return manual;
    const auto = classifyCountry(geo?.country_code || geo?.country_name);
    if (auto) return auto;
    if (typeof navigator !== "undefined") {
      const lang = (navigator.language || "").toLowerCase();
      if (lang.startsWith("en-us")) return "us";
      if (lang.startsWith("en-gb") || lang.startsWith("en-ie") || lang.startsWith("en-au") ||
          lang.startsWith("en-nz") || lang.startsWith("en-za") || lang.startsWith("en-in") ||
          lang.startsWith("en-ca")) return "uk";
    }
    return "us";
  }, [manualCountry, geo?.country_code, geo?.country_name]);
}

/** Localise a single string based on the current visitor's locale. */
export function useLocalisedText(text) {
  const locale = useEnglishLocale();
  return useMemo(() => localiseSpelling(text, locale), [text, locale]);
}
